import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Lock, Clock, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            await axios.post(`${API}/auth/reset-password`, { 
                token, 
                new_password: password 
            });
            setIsSuccess(true);
            toast.success('Password reset successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Reset link expired or invalid');
        } finally {
            setIsLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-slate-50 flex flex-col items-center justify-center px-6">
                <div className="text-center">
                    <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">
                        Invalid Reset Link
                    </h2>
                    <p className="text-muted-foreground mb-6">
                        This password reset link is invalid or has expired.
                    </p>
                    <Link
                        to="/forgot-password"
                        className="text-[#1C6EE7] font-semibold hover:underline"
                    >
                        Request a new reset link
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-slate-50 flex flex-col">
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                {/* Logo */}
                <div className="flex flex-col items-center mb-6">
                    <img 
                        src="/voilaw-logo.png" 
                        alt="Voilaw Logo" 
                        className="h-32 w-32 object-contain"
                    />
                    <h1 className="font-brand text-3xl text-foreground mt-4">Voilaw</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Time & task tracking for attorneys
                    </p>
                </div>

                {isSuccess ? (
                    <div className="w-full max-w-sm text-center">
                        <div className="p-4 bg-emerald-100 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                            <CheckCircle className="h-10 w-10 text-emerald-600" />
                        </div>
                        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                            Password Reset!
                        </h2>
                        <p className="text-muted-foreground mb-8">
                            Your password has been successfully reset. You can now sign in with your new password.
                        </p>
                        <Button
                            onClick={() => navigate('/login')}
                            className="w-full sm:w-3/4 mx-auto block h-14 rounded-full text-lg font-semibold bg-[#1C6EE7] hover:bg-[#1559c7]"
                        >
                            Sign in
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className="text-center mb-10 max-w-sm">
                            <div className="p-4 bg-blue-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                <Lock className="h-8 w-8 text-[#1C6EE7]" />
                            </div>
                            <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                                Set new password
                            </h2>
                            <p className="text-muted-foreground">
                                Enter your new password below.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-foreground">New Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="reset-password-input"
                                    className="h-14 rounded-2xl bg-white shadow-soft border-0 text-lg px-5"
                                />
                                <p className="text-xs text-muted-foreground px-1">
                                    Minimum 6 characters
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="text-foreground">Confirm Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    data-testid="reset-confirm-password-input"
                                    className="h-14 rounded-2xl bg-white shadow-soft border-0 text-lg px-5"
                                />
                            </div>
                            
                            <div className="pt-4">
                                <Button
                                    type="submit"
                                    className="w-full sm:w-3/4 mx-auto block h-14 rounded-full text-lg font-semibold bg-[#1C6EE7] hover:bg-[#1559c7] shadow-lg hover:shadow-xl transition-all"
                                    disabled={isLoading}
                                    data-testid="reset-password-submit-btn"
                                >
                                    {isLoading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Clock className="h-5 w-5 animate-spin" />
                                            Resetting...
                                        </span>
                                    ) : (
                                        'Reset Password'
                                    )}
                                </Button>
                            </div>
                        </form>

                        <div className="mt-8 text-center">
                            <Link
                                to="/login"
                                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back to sign in
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
